/**
 * Hooks for the platform console's community tabs (Moderation, Badges). The
 * endpoints live under /api/admin/community and answer 404 to anyone without
 * the platform-admin flag — the console page itself also redirects them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BadgeGrant,
  BadgeGrantList,
  GrantBadgeRequest,
  ModerationAction,
  ModerationQueue,
  ModerationQueueItem,
} from "@shared/types/communityAdmin";
import { apiClient, ApiError } from "../../../lib/api/client";

export type ModerationView = "pending" | "decided";

const keys = {
  moderation: ["admin", "community", "moderation"] as const,
  queue: (view: ModerationView) => ["admin", "community", "moderation", view] as const,
  badges: ["admin", "community", "badges"] as const,
};

export function useModerationQueue(view: ModerationView) {
  return useQuery<ModerationQueue, ApiError>({
    queryKey: keys.queue(view),
    queryFn: () => apiClient<ModerationQueue>(`/api/admin/community/moderation?view=${view}`),
  });
}

export function useModerationDecision() {
  const qc = useQueryClient();
  return useMutation<ModerationQueueItem, ApiError, { itemId: string; action: ModerationAction; note?: string }>({
    mutationFn: ({ itemId, action, note }) =>
      apiClient<ModerationQueueItem>(`/api/admin/community/moderation/${encodeURIComponent(itemId)}`, {
        method: "POST",
        body: JSON.stringify({ action, note }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.moderation }),
  });
}

export function useBadgeGrants() {
  return useQuery<BadgeGrantList, ApiError>({
    queryKey: keys.badges,
    queryFn: () => apiClient<BadgeGrantList>("/api/admin/community/badges"),
  });
}

export function useGrantBadge() {
  const qc = useQueryClient();
  return useMutation<BadgeGrant, ApiError, GrantBadgeRequest>({
    mutationFn: (body) =>
      apiClient<BadgeGrant>("/api/admin/community/badges", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.badges }),
  });
}

export function useRevokeBadge() {
  const qc = useQueryClient();
  return useMutation<BadgeGrant, ApiError, { grantId: string }>({
    mutationFn: ({ grantId }) =>
      apiClient<BadgeGrant>(`/api/admin/community/badges/${encodeURIComponent(grantId)}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.badges }),
  });
}
